import { Eye, EyeOff, LogIn } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface LoginScreenProps {
  onLogin: (username: string) => void
}

export function LoginScreen({ onLogin }: LoginScreenProps) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!username.trim() || !password.trim()) return

    setIsLoading(true)

    // Simulate login delay for demo
    setTimeout(() => {
      setIsLoading(false)
      onLogin(username.trim())
    }, 800)
  }

  return (
    <div className="min-h-screen bg-gradient-hero flex flex-col">
      {/* Header area with logo */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 pt-12 pb-8">
        <div className="w-20 h-20 rounded-2xl bg-primary-foreground flex items-center justify-center mb-6 shadow-lg">
          <span className="text-primary text-3xl font-bold">R</span>
        </div>

        <h1 className="text-2xl font-bold text-primary-foreground mb-2">REINZ Companion</h1>
        <p className="text-primary-foreground/70 text-sm text-center max-w-xs">
          Everything a New Zealand real estate agent needs — in one smart app
        </p>
      </div>

      {/* Login form */}
      <div className="bg-background rounded-t-3xl px-6 pt-8 pb-12 safe-bottom">
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="text-center mb-6">
            <h2 className="text-xl font-semibold text-foreground">Welcome back</h2>
            <p className="text-sm text-muted-foreground mt-1">Sign in to your REINZ account</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="username" className="text-sm font-medium">
              Username or Email
            </Label>
            <Input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username"
              className="h-12 text-base"
              autoComplete="username"
              autoCapitalize="none"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm font-medium">
              Password
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="h-12 text-base pr-12"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="flex justify-end">
            <button type="button" className="text-sm text-primary hover:underline">
              Forgot password?
            </button>
          </div>

          <Button
            type="submit"
            className="w-full h-12 text-base font-medium"
            disabled={!username.trim() || !password.trim() || isLoading}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                Signing in...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <LogIn className="w-5 h-5" />
                Sign In
              </span>
            )}
          </Button>

          <p className="text-center text-xs text-muted-foreground mt-6">For REINZ members only</p>
        </form>

        {/* Demo hint */}
        <div className="mt-6 p-3 bg-secondary/50 rounded-lg">
          <p className="text-xs text-muted-foreground text-center">
            <span className="font-medium">Demo mode:</span> Enter any username and password to
            continue
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center mt-8 text-xs text-muted-foreground">
          <span>Powered by </span>
          <span className="ml-1 font-semibold text-primary">REINZ</span>
        </div>
      </div>
    </div>
  )
}
